import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'v5/design-system/Badge';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { cn } from 'v5/platform/dom/cn';
import { useOverflowContainer } from 'v5/platform/react/useOverflowContainer';

interface BadgeValue {
  key: string;
  value: string;
  icon: ComponentType<{ className?: string }> | null;
}

/**
 * A row's badges, whole ones only: as many as fit from the first list on, the rest in one "+N"
 * that names them, worked out again as the row's width changes. Only the first badge shortens,
 * when it alone is wider than 120px. The values come without empty ones. Takes the room the row
 * leaves it and stands at its right end.
 */
export function ResultBadges({
  lists
}: {
  lists: {
    key: string;
    values: string[];
    icon: ComponentType<{ className?: string }> | null;
  }[];
}) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  const values = lists.flatMap(list =>
    list.values.map(
      (value, index): BadgeValue => ({
        key: `${list.key}:${index}`,
        value,
        icon: index === 0 ? list.icon : null
      })
    )
  );
  const overflow = useOverflowContainer({
    items: values,
    getItemKey: item => item.key,
    rowClassName: 'flex items-center gap-1',
    // never narrower than the "+N" alone
    containerClassName: 'min-w-[36px] flex-1 justify-end',
    renderItem: ({ item: { value, icon: Icon }, index }) => (
      <Badge
        variant="gray"
        tone="subtle"
        size="sm"
        className={cn(index === 0 && 'max-w-[120px]')}
      >
        {Icon && <Icon className="size-3.5 shrink-0" />}
        <span className={cn(index === 0 && 'truncate')}>{value}</span>
      </Badge>
    ),
    renderTrigger: ({ context, overflowItems }) => {
      const more = (
        <Badge variant="gray" tone="subtle" size="sm">
          {t('more', { count: overflowItems.length })}
        </Badge>
      );
      if (context === 'measure') return more;
      const names = overflowItems.map(entry => entry.item.value).join(', ');
      return (
        <SimpleTooltip title={names}>
          <span className="inline-flex">
            {more}
            <span className="sr-only">{names}</span>
          </span>
        </SimpleTooltip>
      );
    }
  });
  if (values.length === 0) return <span className="flex-1" />;
  return (
    <div {...overflow.containerProps} data-result-badges="">
      {overflow.content}
    </div>
  );
}
